/**
 * DiscountsPanel — standalone discount-code management.
 *
 * Surfaces the same Stripe-validated promo-code flow that lives under
 * /admin?tab=academy → Discounts, but as a top-level tab visible to ALL
 * admin roles (super, systems, services, academy). Codes are global —
 * any admin can mint one for a student/parent who asks.
 *
 * Backend endpoints used:
 *   GET    /api/academy/admin/discounts              — list
 *   POST   /api/academy/admin/discounts              — create
 *   PATCH  /api/academy/admin/discounts/{code}?active=…
 *   DELETE /api/academy/admin/discounts/{code}
 *
 * Backend already gates these with `require_admin`, so any admin role
 * is permitted — no new RBAC wiring required here.
 */
import React, { useEffect, useState } from 'react';
import { KeyRound, Plus, Trash2, Copy, Check, X } from 'lucide-react';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

async function jget(path)            { const r = await fetch(`${API}${path}`, { credentials: 'include' });                if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }
async function jpost(path, body)     { const r = await fetch(`${API}${path}`, { method: 'POST',   credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }); if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || `HTTP ${r.status}`); } return r.json(); }
async function jpatch(path)          { const r = await fetch(`${API}${path}`, { method: 'PATCH',  credentials: 'include' }); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }
async function jdelete(path)         { const r = await fetch(`${API}${path}`, { method: 'DELETE', credentials: 'include' }); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }

function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* ignore */ }
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#a8871a] bg-amber-50 hover:bg-amber-100"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function DiscountsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const reload = async () => {
    setLoading(true); setErr('');
    try { const d = await jget('/academy/admin/discounts'); setRows(d.rows || []); }
    catch (e) { setErr(e.message || 'Failed to load discounts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (code, active) => { try { await jpatch(`/academy/admin/discounts/${code}?active=${active}`); reload(); } catch (e) { setErr(e.message); } };
  const remove = async (code) => {
    if (!window.confirm(`Permanently delete code "${code}"?\nExisting checkouts that already used it are unaffected.`)) return;
    try { await jdelete(`/academy/admin/discounts/${code}`); reload(); } catch (e) { setErr(e.message); }
  };

  return (
    <section data-testid="discounts-panel" className="flex flex-col gap-4">
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <header className="px-6 py-4 flex items-center gap-2 bg-gradient-to-r from-zinc-950 to-zinc-800 text-white">
          <KeyRound className="w-4 h-4 text-[#f5a623]" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Promotions · Stripe-validated</div>
            <h3 className="text-[15px] font-bold">Discount codes</h3>
            <p className="text-[11px] text-white/60 mt-0.5">
              Create promotional codes to share with students, parents or partners. Codes are validated at Stripe checkout —
              recipients can paste them on the enrollment / checkout page or you can append <code className="font-mono">?promo=CODE</code> to the share link.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="discounts-panel-create-btn"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] hover:opacity-90"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
          >
            <Plus className="w-3 h-3" /> New code
          </button>
        </header>

        <div className="p-4">
          {err && (
            <div className="mb-3 px-3 py-2 rounded-md text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
              {err}
            </div>
          )}
          {loading ? (
            <div className="text-[12px] text-gray-500 p-2">Loading…</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-[12.5px]" data-testid="discounts-panel-table">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Code</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">% off</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Used</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Expires</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Programs</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">State</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500" data-testid="discounts-panel-empty">No discount codes yet — create one to share with someone who asks.</td></tr>
                  ) : rows.map((d) => (
                    <tr key={d.code} data-testid={`discounts-panel-row-${d.code}`}>
                      <td className="px-3 py-2 font-mono font-bold text-gray-900">{d.code}</td>
                      <td className="px-3 py-2 font-extrabold text-[#a8871a]">{d.percent}%</td>
                      <td className="px-3 py-2 text-gray-600">{d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ''}</td>
                      <td className="px-3 py-2 text-gray-600">{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2 text-gray-600 text-[11px]">{d.program_slugs?.length ? `${d.program_slugs.length} program${d.program_slugs.length === 1 ? '' : 's'}` : 'All'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${d.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                          {d.active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <CopyChip value={d.code} />
                        <button
                          onClick={() => toggle(d.code, !d.active)}
                          className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-gray-800 bg-gray-50 hover:bg-gray-100"
                        >
                          {d.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => remove(d.code)}
                          className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-rose-700 hover:bg-rose-50"
                          data-testid={`discounts-panel-delete-${d.code}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateDiscountModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); reload(); }} />}
    </section>
  );
}

function CreateDiscountModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ code: '', percent: 10, max_uses: '', expires_at: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await jpost('/academy/admin/discounts', {
        code: form.code.trim(),
        percent: Number(form.percent),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
        program_slugs: null,
        note: form.note || null,
      });
      onSaved();
    } catch (err) { setError(err.message || 'Create failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40" data-testid="discounts-panel-create-modal">
      <div className="w-full max-w-[460px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <header className="px-5 py-3 flex items-center gap-2 bg-gradient-to-r from-zinc-950 to-zinc-800 text-white">
          <KeyRound className="w-4 h-4 text-[#f5a623]" />
          <h3 className="text-[14px] font-bold flex-1">New discount code</h3>
          <button onClick={onClose} className="p-1 text-white/70 hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-1.5">Code</label>
              <input
                required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="EARLYBIRD15" pattern="[A-Z0-9_-]+" maxLength={40}
                data-testid="discounts-panel-modal-code"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#c9a227] outline-none text-[13.5px] font-mono uppercase"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-1.5">Percent off</label>
              <input
                type="number" required min={1} max={90} value={form.percent}
                onChange={(e) => setForm({ ...form, percent: e.target.value })}
                data-testid="discounts-panel-modal-percent"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#c9a227] outline-none text-[13.5px]"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-1.5">Max uses (blank = unlimited)</label>
              <input
                type="number" min={1} value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#c9a227] outline-none text-[13.5px]"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-1.5">Expires (blank = never)</label>
              <input
                type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ''}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#c9a227] outline-none text-[13.5px]"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-1.5">Note (internal)</label>
            <input
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Why this code exists — shows in audit log"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#c9a227] outline-none text-[13.5px]"
            />
          </div>
          {error && <div className="text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md">{error}</div>}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider text-gray-600 hover:text-gray-900">Cancel</button>
            <button
              type="submit" disabled={busy}
              data-testid="discounts-panel-modal-submit"
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 hover:opacity-90"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
            >
              {busy ? 'Creating…' : 'Create code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
